import { createEntityAdapter, createSlice, type EntityState, type PayloadAction } from '@reduxjs/toolkit'

import { sharedHydrated } from '@/store/shared-actions'

import type { DesignVariation, DesignVersion, MasterTemplate, WorkingCopy } from './types'

export const mastersAdapter = createEntityAdapter<MasterTemplate>()
export const versionsAdapter = createEntityAdapter<DesignVersion>()
export const variationsAdapter = createEntityAdapter<DesignVariation>()
export const workingCopiesAdapter = createEntityAdapter<WorkingCopy>()

export interface DesignsState {
  masters: EntityState<MasterTemplate, string>
  versions: EntityState<DesignVersion, string>
  variations: EntityState<DesignVariation, string>
  workingCopies: EntityState<WorkingCopy, string>
}

export const emptyDesigns = (): DesignsState => ({
  masters: mastersAdapter.getInitialState(),
  versions: versionsAdapter.getInitialState(),
  variations: variationsAdapter.getInitialState(),
  workingCopies: workingCopiesAdapter.getInitialState(),
})

/**
 * Masters, versions and variations are only ever added: a new version never rewrites an earlier one, and the
 * original master is never edited. Working copies are the one editable thing, and they are copies.
 */
export const designsSlice = createSlice({
  name: 'designs',
  initialState: emptyDesigns(),
  reducers: {
    masterAdded(state, action: PayloadAction<MasterTemplate>) {
      mastersAdapter.addOne(state.masters, action.payload)
    },
    versionAdded(state, action: PayloadAction<DesignVersion>) {
      versionsAdapter.addOne(state.versions, action.payload)
    },
    variationsAdded(state, action: PayloadAction<DesignVariation[]>) {
      variationsAdapter.addMany(state.variations, action.payload)
    },
    workingCopySaved(state, action: PayloadAction<WorkingCopy>) {
      workingCopiesAdapter.upsertOne(state.workingCopies, action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sharedHydrated, (_state, action) => action.payload.data.designs)
  },
})

export const { masterAdded, versionAdded, variationsAdded, workingCopySaved } = designsSlice.actions
